<script setup lang="ts">
import { computed, ref, watch, useSlots } from 'vue'
import { useI18n } from 'vue-i18n'
import axios from 'axios'
import {
  Dialog,
  DialogScrollContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Button } from '@/shadcn/components/ui/button'
import { Input } from '@/shadcn/components/ui/input'
import type { ButtonVariants } from '@/shadcn/components/ui/button'
import type { ConfirmDialogProps, ConfirmDialogEmits } from './types'

const props = withDefaults(defineProps<ConfirmDialogProps>(), {
  severity: 'info',
  mode: 'form',
  method: 'POST',
  requireConfirmation: false,
  size: 'sm',
  open: undefined,
})

const emit = defineEmits<ConfirmDialogEmits>()

const { t } = useI18n()
const slots = useSlots()

const isOpen = ref(props.open ?? false)
watch(() => props.open, (value) => {
  if (value !== undefined) isOpen.value = value
})
const setOpen = (value: boolean) => {
  isOpen.value = value
  emit('update:open', value)
}

const loading = ref(false)
const typed = ref('')
const formRef = ref<HTMLFormElement | null>(null)

watch(isOpen, (value) => {
  if (!value) typed.value = ''
})

const buttonVariant = computed<ButtonVariants['variant']>(() => {
  switch (props.severity) {
    case 'danger':
      return 'destructive'
    case 'success':
      return 'success'
    case 'warning':
      return 'warning'
    default:
      return 'default'
  }
})

const csrfToken = computed<string>(() => window.Laravel?.csrfToken ?? '')

const confirmDisabled = computed(() => {
  if (loading.value) return true
  if (props.requireConfirmation) {
    return typed.value.trim() !== (props.confirmationText ?? '').trim()
  }
  return false
})

/** Translate display text through vue-i18n (keys live in resources/js/locale). */
const tr = (value?: string) => (value ? t(value) : '')

const resolvedTitle = computed(() => tr(props.title))
const resolvedMessage = computed(() => tr(props.message ?? props.description))
const resolvedConfirmLabel = computed(() => (props.confirmLabel ? tr(props.confirmLabel) : t('Confirm')))
const resolvedCancelLabel = computed(() => (props.cancelLabel ? tr(props.cancelLabel) : t('Cancel')))

const onConfirm = async () => {
  if (confirmDisabled.value) return
  emit('confirmed')

  if (!props.action) {
    setOpen(false)
    return
  }

  if (props.mode === 'form') {
    formRef.value?.submit()
    return
  }

  // mode === 'request' — global axios already carries the CSRF header.
  loading.value = true
  try {
    const { data } = await axios.request({
      url: props.action,
      method: props.method,
    })
    emit('success', data)
    setOpen(false)
  } catch (error) {
    emit('error', error)
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <Dialog :open="isOpen" @update:open="setOpen">
    <DialogTrigger v-if="slots.trigger" as-child>
      <slot name="trigger" />
    </DialogTrigger>

    <DialogScrollContent :size="size">
      <DialogHeader>
        <DialogTitle>{{ resolvedTitle }}</DialogTitle>
        <DialogDescription v-if="resolvedMessage || slots.default">
          <slot>{{ resolvedMessage }}</slot>
        </DialogDescription>
      </DialogHeader>

      <div v-if="requireConfirmation" class="tw:flex tw:flex-col tw:gap-2">
        <label class="tw:text-sm tw:text-muted-foreground">
          {{ t('Type {text} to confirm', { text: confirmationText }) }}
        </label>
        <Input v-model="typed" :placeholder="confirmationText" />
      </div>

      <DialogFooter>
        <Button type="button" variant="secondary" @click="setOpen(false)">
          {{ resolvedCancelLabel }}
        </Button>

        <form
          v-if="mode === 'form' && action"
          ref="formRef"
          :action="action"
          method="post"
          class="tw:contents"
        >
          <input type="hidden" name="_token" :value="csrfToken">
          <input v-if="method !== 'POST'" type="hidden" name="_method" :value="method">
          <Button
            type="button"
            :variant="buttonVariant"
            :disabled="confirmDisabled"
            @click="onConfirm"
          >
            {{ resolvedConfirmLabel }}
          </Button>
        </form>

        <Button
          v-else
          type="button"
          :variant="buttonVariant"
          :disabled="confirmDisabled"
          :loading="loading"
          @click="onConfirm"
        >
          {{ resolvedConfirmLabel }}
        </Button>
      </DialogFooter>
    </DialogScrollContent>
  </Dialog>
</template>
